/**
 * Menu Theme Context
 * 
 * Provides theme customization data to all menu components
 */

import React, { createContext, useContext, useMemo } from 'react';
import type { Branch } from '@/types/index';
import type { MenuCustomization } from '@/types/menuCustomization';
import { branchToCustomization } from '@/utils/menuCustomizationConverter';
import {
  getCardContainerClasses,
  getCardContainerStyles,
  getHeadingClasses,
  getBodyTextClasses,
  getTextStyles,
  getLayoutGridClasses,
  getBackgroundStyles,
  getBackgroundOverlayStyles,
  getPrimaryButtonClasses,
  getPrimaryButtonStyles,
  getContainerMaxWidthClass,
  getImageAspectRatioClass,
} from '@/utils/menuStyles';

// ============================================================================
// Context Type
// ============================================================================

interface MenuThemeContextValue {
  /** Full customization configuration */
  customization: MenuCustomization;
  
  /** Branch data */
  branch: Branch;
  
  // Convenience methods for getting styles
  getCardClasses: () => string;
  getCardStyles: () => React.CSSProperties;
  getHeadingClasses: (level?: 'h1' | 'h2' | 'h3') => string;
  getBodyClasses: (secondary?: boolean) => string;
  getTextStyles: (secondary?: boolean) => React.CSSProperties;
  getGridClasses: () => string;
  getBackgroundStyles: () => React.CSSProperties;
  getBackgroundOverlay: () => React.CSSProperties | null;
  getButtonClasses: () => string;
  getButtonStyles: () => React.CSSProperties;
  getContainerClass: () => string;
  getImageAspectClass: () => string;
}

// ============================================================================
// Context Creation
// ============================================================================

const MenuThemeContext = createContext<MenuThemeContextValue | null>(null);

// ============================================================================
// Provider Component
// ============================================================================

interface MenuThemeProviderProps {
  branch: Branch;
  children: React.ReactNode;
  /** Optional partial customization override */
  customizationOverride?: Partial<MenuCustomization>;
}

export const MenuThemeProvider: React.FC<MenuThemeProviderProps> = ({
  branch,
  children,
  customizationOverride,
}) => {
  // Convert branch to customization and memoize
  const customization = useMemo(() => {
    const base = branchToCustomization(branch);
    
    // Apply override if provided
    if (customizationOverride) {
      return {
        ...base,
        ...customizationOverride,
        colors: { ...base.colors, ...customizationOverride.colors },
        typography: { ...base.typography, ...customizationOverride.typography },
        spacing: { ...base.spacing, ...customizationOverride.spacing },
        effects: { ...base.effects, ...customizationOverride.effects },
        layout: { ...base.layout, ...customizationOverride.layout },
        background: { ...base.background, ...customizationOverride.background },
        components: {
          header: { ...base.components.header, ...customizationOverride.components?.header },
          categoryTabs: { ...base.components.categoryTabs, ...customizationOverride.components?.categoryTabs },
          menuCard: { ...base.components.menuCard, ...customizationOverride.components?.menuCard },
          floatingCart: { ...base.components.floatingCart, ...customizationOverride.components?.floatingCart },
        },
      };
    }
    
    return base;
  }, [branch, customizationOverride]);
  
  // Memoize convenience methods
  const value = useMemo<MenuThemeContextValue>(() => ({
    customization,
    branch,
    getCardClasses: () => getCardContainerClasses(customization),
    getCardStyles: () => getCardContainerStyles(customization),
    getHeadingClasses: (level) => getHeadingClasses(customization, level),
    getBodyClasses: (secondary) => getBodyTextClasses(customization, secondary),
    getTextStyles: (secondary) => getTextStyles(customization, secondary),
    getGridClasses: () => getLayoutGridClasses(customization),
    getBackgroundStyles: () => getBackgroundStyles(customization),
    getBackgroundOverlay: () => getBackgroundOverlayStyles(customization),
    getButtonClasses: () => getPrimaryButtonClasses(customization),
    getButtonStyles: () => getPrimaryButtonStyles(customization),
    getContainerClass: () => getContainerMaxWidthClass(customization),
    getImageAspectClass: () => getImageAspectRatioClass(customization),
  }), [customization, branch]);
  
  return (
    <MenuThemeContext.Provider value={value}>
      {children}
    </MenuThemeContext.Provider>
  );
};

// Export context for hooks
export { MenuThemeContext };

